import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {InputStatusIcon} from '@/components/ui/input-status-icon/InputStatusIcon'
import {Label} from '@/components/ui/label/Label'
import {
    SelectComboboxInput,
    SelectComboboxList,
    SelectComboboxPopover,
    SelectItem,
    SelectTrigger,
    SelectVirtualList
} from '@/components/ui/select-atoms/SelectAtoms'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {formatDate} from '@/dayjs'
import {useTranslation} from '@/translations/i18n'
import {ComboboxProvider, SelectProvider} from '@ariakit/react'
import {useMemo, useRef, useState} from 'react'
import {useGetInfiniteMedicalReportsByPatient} from '@/features/medical-reports/queries/useGetInfiniteMedicalReportsByPatient'
import {useInputSearch} from '@/hooks/useInputSearch'
import {XCloseIcon} from '@/components/ui/icon/Icon'
import {MedicalReportSmall} from '@/features/medical-reports/types'

interface InputSelectMedicalReportByPatientProps {
    patientId: string
    disabled?: boolean
    label?: string
    value?: {id: string; name: string} | null
    onChange: (value: MedicalReportSmall | null) => void
    placeholder?: string
    errorMessage?: string
    onClear?: () => void
}

export const InputSelectMedicalReportByPatient = ({
    patientId,
    disabled,
    label,
    onChange,
    errorMessage,
    placeholder,
    onClear,
    value
}: InputSelectMedicalReportByPatientProps) => {
    const {t} = useTranslation()
    const [isQueryEnabled, setIsQueryEnabled] = useState(false)
    const inputMedicalReportRef = useRef(null)
    const {searchValue, onSearch, onResetSearch} = useInputSearch()
    const {
        remappedData: medicalReports,
        isLoading,
        isError,
        hasNextPage,
        fetchNextPage
    } = useGetInfiniteMedicalReportsByPatient({
        urlParams: {patientId},
        params: {
            search: searchValue,
            limit: 10
        },
        options: {enabled: !disabled && isQueryEnabled}
    })

    const medicalReportName = useMemo(
        () => (value?.name ? value.name : value?.id ? `Relazione ${value.id}` : t('commons:select')),
        [value?.id, value?.name, t]
    )

    return (
        <ComboboxProvider setValue={onSearch} resetValueOnHide>
            <SelectProvider
                value={value?.id ?? ''}
                setValue={value => onChange(medicalReports.find(report => report && report.id == value) ?? null)}
                setOpen={isOpen => {
                    onResetSearch(inputMedicalReportRef)
                    setIsQueryEnabled(isOpen)
                }}
            >
                <Flexbox direction="column" gap={1.5} fullWidth>
                    {label && <Label>{label}</Label>}
                    <SelectTrigger
                        placeholder={placeholder}
                        fullWidth
                        value={value?.id}
                        displayValue={() => (
                            <Flexbox justify="space-between" align="center" gap={2} fullWidth>
                                {medicalReportName}
                                {onClear && (
                                    <XCloseIcon
                                        role="button"
                                        onClick={e => {
                                            e.preventDefault()
                                            e.stopPropagation()
                                            onClear()
                                        }}
                                    />
                                )}
                            </Flexbox>
                        )}
                        hasError={!!errorMessage}
                        disabled={disabled}
                    />

                    {errorMessage && (
                        <Flexbox align="center" gap={1.5}>
                            <InputStatusIcon hasError={!!errorMessage} />
                            <InputHelpText error={errorMessage} />
                        </Flexbox>
                    )}

                    <SelectComboboxPopover>
                        <SelectComboboxInput
                            ref={inputMedicalReportRef}
                            placeholder={t('commons:search_placeholder')}
                        />
                        <SelectComboboxList>
                            {isLoading ? (
                                Array.from({length: 6}, (_, index) => <Skeleton key={index} height={32} />)
                            ) : isError ? (
                                <InputHelpText error={t('errors:default')} />
                            ) : medicalReports.length > 0 ? (
                                <SelectVirtualList
                                    data={medicalReports}
                                    totalCount={medicalReports.length}
                                    overscan={100}
                                    endReached={() => hasNextPage && fetchNextPage()}
                                    itemContent={(itemIndex, item) => (
                                        <SelectItem hasItemCheckbox key={itemIndex} value={item.id}>
                                            {item.date && item.medicalRecordReportType
                                                ? `${item.medicalRecordReportType.name} - ${formatDate(item.date, 'DD/MM/YYYY')}`
                                                : item.id && `Relazione ${item.id}`}
                                        </SelectItem>
                                    )}
                                />
                            ) : (
                                <InputHelpText helpText={t('commons:no_results')} />
                            )}
                        </SelectComboboxList>
                    </SelectComboboxPopover>
                </Flexbox>
            </SelectProvider>
        </ComboboxProvider>
    )
}
